/**
 * Phase 10 — Payment Request card (one row per request on SR detail).
 *
 * Shows: amount, type, status pill, delivery channel icons, created date,
 * copy-link button, cancel button on pending rows.
 */
import { useState } from "react";
import { toast } from "sonner";
import {
  EnvelopeIcon,
  ChatCircleTextIcon,
  CopyIcon,
  ArrowSquareOutIcon,
} from "@phosphor-icons/react";

import {
  PAYMENT_STATUS_LABEL,
  PAYMENT_STATUS_TINT,
  PAYMENT_TYPE_LABEL,
  formatDollars,
  cancelPaymentRequest,
} from "../../lib/paymentRequests";

function DeliveryGlyph({ method }) {
  if (method === "email") {
    return <EnvelopeIcon size={12} className="text-slate-500" title="Email" />;
  }
  if (method === "sms") {
    return (
      <ChatCircleTextIcon
        size={12}
        className="text-emerald-600"
        title="SMS"
      />
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5">
      <EnvelopeIcon size={12} className="text-slate-500" />
      <ChatCircleTextIcon size={12} className="text-emerald-600" />
    </span>
  );
}

export default function PaymentRequestCard({ pr, onChange }) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pr.stripe_payment_link_url);
      toast.success("Payment link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      const updated = await cancelPaymentRequest(pr.id);
      toast.success("Payment request cancelled");
      onChange(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  const tint =
    PAYMENT_STATUS_TINT[pr.status] || PAYMENT_STATUS_TINT.cancelled;

  return (
    <div
      data-testid={`payment-request-card-${pr.id}`}
      className="bg-white border border-slate-200 rounded-md p-4 flex items-start gap-4 shadow-card"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-heading text-base font-semibold text-slate-900">
            {PAYMENT_TYPE_LABEL[pr.payment_type]} · {formatDollars(pr.amount_cents)}
          </span>
          <span
            data-testid={`payment-request-status-${pr.id}`}
            className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${tint}`}
          >
            {PAYMENT_STATUS_LABEL[pr.status] || pr.status}
          </span>
        </div>
        <div className="mt-1 text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
          <DeliveryGlyph method={pr.delivery_method} />
          <span>
            Created {new Date(pr.created_at).toLocaleDateString()}{" "}
            {new Date(pr.created_at).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        </div>
        {pr.description && (
          <div className="mt-2 text-[12px] text-slate-700 leading-relaxed">
            {pr.description}
          </div>
        )}
        <div className="mt-2 flex items-center gap-2">
          <code className="text-[11px] font-data text-slate-500 truncate max-w-full">
            {pr.stripe_payment_link_url}
          </code>
          <button
            type="button"
            onClick={copy}
            data-testid={`payment-request-copy-${pr.id}`}
            title="Copy payment link"
            className="inline-flex items-center rounded-md border border-slate-200 px-1.5 py-1 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
          >
            <CopyIcon size={12} />
          </button>
          <a
            href={pr.stripe_payment_link_url}
            target="_blank"
            rel="noreferrer"
            title="Open in new tab"
            className="inline-flex items-center rounded-md border border-slate-200 px-1.5 py-1 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
          >
            <ArrowSquareOutIcon size={12} />
          </a>
        </div>
      </div>

      {pr.status === "pending" && (
        <div className="shrink-0">
          {!confirming ? (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              data-testid={`payment-request-cancel-${pr.id}`}
              className="text-[11px] font-semibold text-rose-700 hover:underline"
            >
              Cancel
            </button>
          ) : (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={cancel}
                disabled={busy}
                data-testid={`payment-request-cancel-confirm-${pr.id}`}
                className="rounded-md bg-rose-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
              >
                {busy ? "…" : "Confirm"}
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="rounded-md border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50"
              >
                No
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
